import { NewFeatures } from "@/components/tooltip/new-features.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  Box,
  ButtonIcon,
  FontAwesomeIcon,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { ComponentProps, ReactNode, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

const NEW_FEATURE_VISIBILITY_DAYS = 7;

const newFeatureLaunchDates: Record<NewFeatures, string> = {
  [NewFeatures.mass_assign]: "2024-11-06",
  [NewFeatures.reporting_tab]: "2025-01-29",
  [NewFeatures.reporting_ats_disqualification_new_filter]: "2025-12-15",
  [NewFeatures.reporting_ats_stage_flow_candidates_export]: "2025-12-16",
  [NewFeatures.ats_stage_flow_funnel]: "2026-03-17",
  [NewFeatures.employee_edit_menu]: "2026-04-05",
  [NewFeatures.employee_saved_views]: "2026-07-24",
  [NewFeatures.employee_termination_in_actions]: "2026-04-09",
  [NewFeatures.event_time_minute_precision]: "2026-08-12",
};

const isNewFeatureExpired = (feature: NewFeatures): boolean => {
  const expiresAt = new Date(newFeatureLaunchDates[feature]);
  expiresAt.setDate(expiresAt.getDate() + NEW_FEATURE_VISIBILITY_DAYS);
  return Date.now() > expiresAt.getTime();
};

const shouldShowNewFeature = (
  feature: NewFeatures,
  preventShowIfEmployeeIdGreater: number | undefined,
  employee: { id: number } | undefined
): boolean => {
  if (isNewFeatureExpired(feature)) {
    return false;
  }
  if (preventShowIfEmployeeIdGreater && !employee) {
    return false;
  }
  if (preventShowIfEmployeeIdGreater && employee && employee.id > preventShowIfEmployeeIdGreater) {
    return false;
  }
  return !localStorage.getItem(`new-feature-seen-${feature}`);
};

export interface NewFeatureTooltipProps extends ComponentProps<"div"> {
  title: string;
  description: string;
  feature: NewFeatures;
  preventShowIfEmployeeIdGreater?: number;
  children: ReactNode;
}

export const NewFeatureTooltip = ({
  children,
  title,
  feature,
  description,
  preventShowIfEmployeeIdGreater,
}: NewFeatureTooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: employee } = useEmployeeMe();
  const [synced, setSynced] = useState<{
    feature: NewFeatures;
    preventShowIfEmployeeIdGreater?: number;
    employee: typeof employee;
  } | null>(null);

  if (
    !synced ||
    synced.feature !== feature ||
    !Object.is(synced.preventShowIfEmployeeIdGreater, preventShowIfEmployeeIdGreater) ||
    synced.employee !== employee
  ) {
    setSynced({ feature, preventShowIfEmployeeIdGreater, employee });
    if (shouldShowNewFeature(feature, preventShowIfEmployeeIdGreater, employee)) {
      setIsOpen(true);
    }
  }

  useEffect(() => {
    if (shouldShowNewFeature(feature, preventShowIfEmployeeIdGreater, employee)) {
      analyticsService.trackEvent(analyticEvents.pageActions.newFeatureTooltipViewed, {
        [analyticProperties.id]: feature,
      });
    }
  }, [feature, preventShowIfEmployeeIdGreater, employee]);

  const closeTooltip = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsOpen(false);
    localStorage.setItem(`new-feature-seen-${feature}`, "true");
  };

  return (
    <Tooltip delay={false} open={isOpen}>
      <TooltipTrigger>{children}</TooltipTrigger>
      <TooltipContent className={"max-w-96 p-3"}>
        <Stack gap={"xs"}>
          <Stack justify={"between"} items={"center"} direction={"horizontal"}>
            <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
              <Box
                className={twMerge(
                  "text-sm font-semibold px-2 py-1 border rounded-xs align-middle flex items-center bg-light text-dark"
                )}
              >
                <label>NEW</label>
              </Box>
              <Text className="text-light font-semibold">{title}</Text>
            </Stack>
            <ButtonIcon
              onClick={closeTooltip}
              variant={"plain"}
              className={"text-light"}
              icon={<FontAwesomeIcon icon={"fa-light fa-close"} />}
            />
          </Stack>
          <Text className="text-light/80">{description}</Text>
        </Stack>
      </TooltipContent>
    </Tooltip>
  );
};
